import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  useBusinessNoticeSelectList,
  useSendBusinessNotice,
  useBusinessNoticeAlbumList,
} from "~/services/business/notify";
import { getToday } from "%/date";
import useOpenModal from "^/common/useOpenModal";
import {
  type ActivityOption,
  type RecipientOption,
  type NoticeSendSubData,
} from "~/types/common";

export interface GalleryImage {
  ImgID: number;
  ImgURL: string;
  thumbnailUrl?: string | null;
  thumbnailStatus?: string | null;
}

interface AlbumItem {
  notiPhotoSeq: number | null;
  fileSeq: number | null;
  fileUrl: string | null;
  thumbnailUrl?: string | null;
  thumbnailStatus?: string | null;
}

export interface FormData {
  recipient: string;
  animal: string;
  content: string;
  hour: string;
  minute: string;
  program: string;
  activity: string;
  bowel: string;
  meal: string;
  snack: string;
  water: string;
  [key: string]: string | ActivityOption | null; // 동적 필드를 위한 인덱스 시그니처
}

const useNotifySendInform = () => {
  const { data: selectList } = useBusinessNoticeSelectList();
  const sendNoticeMutation = useSendBusinessNotice();
  const albumListMutation = useBusinessNoticeAlbumList();

  // noticeSendSubData 상태 추가
  const [noticeSendSubData, setNoticeSendSubData] = useState<NoticeSendSubData>(
    {
      companyList: [],
      pets: [],
    }
  );

  const [selectListData, setSelectListData] = useState<{
    recipients: RecipientOption[];
    activities: Array<{
      cdActivity: string;
      value: string;
      activityValues: ActivityOption[];
    }>;
  }>({
    recipients: [],
    activities: [],
  });

  // Form 관리
  const formMethods = useForm<FormData>({
    defaultValues: {},
    mode: "onChange",
  });

  const { setValue } = formMethods;

  // 활동 선택 유효성 검사 함수
  const validateActivitySelections = (data: FormData) => {
    if (!isToggle) return true; // 오늘의 활동이 꺼져있으면 검사하지 않음

    console.log("validateActivitySelections - isToggle:", isToggle);
    console.log(
      "validateActivitySelections - activities:",
      selectListData.activities
    );
    console.log("validateActivitySelections - data:", data);

    // 모든 활동 항목이 선택되었는지 확인
    const isValid = selectListData.activities.every((activity) => {
      const selectedValue = data[activity.cdActivity];
      const isSelected =
        selectedValue &&
        typeof selectedValue === "object" &&
        selectedValue !== null;
      console.log(
        `Activity ${activity.cdActivity}:`,
        selectedValue,
        "isSelected:",
        isSelected
      );
      return isSelected;
    });

    console.log("validateActivitySelections - result:", isValid);
    return isValid;
  };

  // 날짜 상태 관리
  const [date] = useState(getToday());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedImgDate, setSelectedImgDate] = useState<Date | null>(null);

  // 모달 상태 관리
  const calendarModal = useOpenModal();
  const imageModal = useOpenModal();
  const imgDateModal = useOpenModal();

  // 이미지 상태 관리
  const [selectedImages, setSelectedImages] = useState<GalleryImage[]>([]);
  const [modalSelectedImages, setModalSelectedImages] = useState<
    GalleryImage[]
  >([]);

  // 앨범 목록 상태
  const [albumData, setAlbumData] = useState<GalleryImage[]>([]);

  // 토글 상태
  const [isToggle, setIsToggle] = useState(false);
  const [isTimeToggle, setIsTimeToggle] = useState(false);

  // 현재 시간을 계산하는 함수 (분은 올림 처리)
  const getCurrentTimeWithRoundedMinutes = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    // 분을 올림 처리 (0-9분 → 10분, 10-19분 → 20분, ..., 50-59분 → 00분+1시간)
    let roundedHour = currentHour;
    let roundedMinute = Math.ceil(currentMinute / 10) * 10;

    // 60분이 되면 1시간 추가하고 분은 0으로
    if (roundedMinute >= 60) {
      roundedHour = (roundedHour + 1) % 24;
      roundedMinute = 0;
    }

    return {
      hour: roundedHour.toString().padStart(2, "0"),
      minute: roundedMinute.toString().padStart(2, "0"),
    };
  };

  // 토글 상태 변경 시 현재 시간 자동 설정
  const handleTimeToggleChange = (checked: boolean) => {
    setIsTimeToggle(checked);

    if (checked) {
      // 토글을 켰을 때 현재 시간 자동 설정
      const currentTime = getCurrentTimeWithRoundedMinutes();
      setValue("hour", currentTime.hour);
      setValue("minute", currentTime.minute);
      console.log(
        `발송시간 자동 설정: ${currentTime.hour}시 ${currentTime.minute}분`
      );
    }
  };

  // 다이얼로그 상태
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState({
    title: "",
    message: "",
  });

  // 앨범 목록 가져오기
  const fetchAlbumList = (selectedRecipient: string) => {
    console.log("selectedRecipient", selectedRecipient);
    if (!selectedRecipient || !selectListData.recipients.length) return;

    const selectedRecipientData = selectListData.recipients.find(
      (recipient) => recipient.petSeq.toString() === selectedRecipient
    );

    if (selectedRecipientData) {
      // 선택된 이미지 날짜가 있으면 사용, 없으면 오늘 날짜 사용
      const dateToUse = selectedImgDate || new Date();
      // 한국 시간대를 고려하여 날짜 변환 (UTC 변환으로 인한 하루 차이 방지)
      const year = dateToUse.getFullYear();
      const month = String(dateToUse.getMonth() + 1).padStart(2, "0");
      const day = String(dateToUse.getDate()).padStart(2, "0");
      const dateString = `${year}${month}${day}`;

      const requestData = {
        date: dateString, // 선택된 날짜 또는 오늘 날짜를 YYYYMMDD 형식으로
        recipients: [
          {
            petSeq: selectedRecipientData.petSeq,
            userSeq: selectedRecipientData.petSeq, // userSeq가 없으므로 petSeq 사용
            petName: selectedRecipientData.petName,
            selected: true,
          },
        ],
      };

      albumListMutation.mutate(requestData, {
        onSuccess: (response) => {
          console.log("앨범 목록 조회 성공:", response);
          // 응답 데이터를 GalleryImage 형태로 변환
          if (response.data && Array.isArray(response.data)) {
            const convertedData = response.data.map((item: AlbumItem) => ({
              ImgID: item.fileSeq || item.notiPhotoSeq || 0,
              ImgURL: item.fileUrl || "",
              thumbnailUrl: item.thumbnailUrl ?? null,
              thumbnailStatus: item.thumbnailStatus ?? null,
            }));
            setAlbumData(convertedData);
          }
        },
        onError: (error) => {
          console.error("앨범 목록 조회 실패:", error);
          // 에러 시 기본 데이터 사용
          setAlbumData([]);
        },
      });
    }
  };

  // selectList 데이터 업데이트
  useEffect(() => {
    if (selectList) {
      const newSelectListData = {
        recipients: selectList.recipients ?? [],
        activities: selectList.activities ?? [],
      };
      setSelectListData(newSelectListData);

      // 동적 activity 필드들의 기본값 설정
      const dynamicDefaults: Record<string, ActivityOption | null> = {};
      newSelectListData.activities.forEach((activity) => {
        dynamicDefaults[activity.cdActivity] = null;
      });

      // 폼 기본값 업데이트 (기존 값 유지하면서 동적 필드 추가)
      const currentValues = formMethods.getValues();
      formMethods.reset({
        ...currentValues,
        ...dynamicDefaults,
      });
    }
  }, [selectList, formMethods]);

  // noticeSendSubData를 사용하여 수신자와 반려동물 옵션 생성
  useEffect(() => {
    if (noticeSendSubData && noticeSendSubData.companyList.length > 0) {
      // 수신자 옵션 생성 (회사별로 그룹화)
      const recipientOptions: RecipientOption[] =
        noticeSendSubData.companyList.map((company) => ({
          petSeq: company.companySeq,
          petName: company.companyName,
          selected: false,
        }));

      // 반려동물 옵션 생성
      const petOptions: RecipientOption[] = noticeSendSubData.pets.map(
        (pet) => ({
          petSeq: pet.petSeq,
          petName: pet.petName,
          selected: false,
        })
      );

      // 기존 selectListData와 병합
      setSelectListData((prev) => ({
        ...prev,
        recipients: [...recipientOptions, ...petOptions],
      }));
    }
  }, [noticeSendSubData]);

  // 폼 제출 로직
  const bindSubmit = (data: FormData, isSave: boolean = false) => {
    console.log("폼 데이터:", data);

    // isTimeToggle이 true인데 sendDateTime이 필수인지 확인
    if (isTimeToggle && (!data.hour || !data.minute)) {
      alert("예약 발송을 선택한 경우 발송 시간을 설정해주세요.");
      return;
    }

    // 날짜와 시간 조합 - ISO 8601 형태로 변환
    const sendDate = selectedDate || new Date();

    // 시간과 분이 유효한지 확인
    const hour = parseInt(data.hour) || 0;
    const minute = parseInt(data.minute) || 0;

    // 유효한 시간 범위인지 확인
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
      alert(
        "유효하지 않은 시간입니다. 시간은 0-23, 분은 0-59 사이여야 합니다."
      );
      return;
    }

    let sendDateTime;
    try {
      const dateTime = new Date(
        sendDate.getFullYear(),
        sendDate.getMonth(),
        sendDate.getDate(),
        hour,
        minute
      );

      // 생성된 Date 객체가 유효한지 확인
      if (isNaN(dateTime.getTime())) {
        throw new Error("Invalid date");
      }

      // 로컬 시간을 그대로 사용 (UTC 변환하지 않음)
      const year = dateTime.getFullYear();
      const month = String(dateTime.getMonth() + 1).padStart(2, "0");
      const day = String(dateTime.getDate()).padStart(2, "0");
      const hours = String(dateTime.getHours()).padStart(2, "0");
      const minutes = String(dateTime.getMinutes()).padStart(2, "0");
      const seconds = String(dateTime.getSeconds()).padStart(2, "0");

      sendDateTime = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.error("날짜/시간 변환 오류:", error);
      alert("날짜/시간 설정에 오류가 발생했습니다. 다시 확인해주세요.");
      return;
    }

    // selectList에서 받아온 데이터 구조를 그대로 유지하면서 사용자가 입력/선택한 값들만 변경
    const updatedSelectList = {
      // 필수 필드들 보장
      bizUserSeq: selectList?.bizUserSeq || 0,
      companySeq: selectList?.companySeq || 0,
      noticeSeq: selectList?.noticeSeq || null,
      notiTitle: typeof data.title === "string" ? data.title : null,
      notiContents: typeof data.content === "string" ? data.content : null,
      dtReservation: isTimeToggle ? sendDateTime : null, // isTimeToggle이 false면 null
      cdSender: selectList?.cdSender || "SENDER_OWNER",
      cdRecipient: selectList?.cdRecipient || "RECIPIENT_PET_OWNER",
      // 백엔드 스펙:
      //  - 저장하기:        tempSaveYn="Y", reservationSendFlag=false
      //  - 즉시 발송:       tempSaveYn="N", reservationSendFlag=false
      //  - 예약 발송(토글ON): tempSaveYn="Y", reservationSendFlag=true
      reservationSendFlag: !isSave && isTimeToggle,
      activityFlag: isToggle, // 오늘의 활동 토글값을 activityFlag에 설정
      tempSaveYn: isSave || isTimeToggle ? "Y" : "N",

      // 스케줄 정보
      schedules: selectList?.schedules || [],

      // 알림장의 selected를 true로 변경
      noticeTypes:
        selectList?.noticeTypes?.map((noticeType) => ({
          ...noticeType,
          selected: noticeType.cdNotiType === "NOTI_DIARY", // 알림장만 true로 설정
        })) || [],

      // 선택된 반려견의 selected를 true로 변경
      recipients:
        selectList?.recipients?.map((recipient) => ({
          ...recipient,
          selected: recipient.petSeq.toString() === data.recipient,
        })) || [],

      // 선택된 활동들의 selected를 true로 변경
      activities:
        selectList?.activities?.map((activity) => {
          const userSelectedValue = data[activity.cdActivity];
          if (userSelectedValue && typeof userSelectedValue === "object") {
            return {
              ...activity,
              activityValues: activity.activityValues.map((option) => ({
                ...option,
                selected:
                  option.cdActivityValue ===
                  (userSelectedValue as ActivityOption).cdActivityValue,
              })),
            };
          }
          return activity;
        }) || [],

      // 선택된 이미지들
      photos: selectedImages.map((img) => ({
        notiPhotoSeq: null,
        fileSeq: img.ImgID,
        fileUrl: img.ImgURL,
      })),
    };

    console.log("전송 요청 데이터:", updatedSelectList);

    // API 호출
    sendNoticeMutation.mutate(updatedSelectList, {
      onSuccess: (response) => {
        console.log(isSave ? "저장 성공:" : "발송 성공:", response);
        const message = isSave
          ? "알림이 성공적으로 저장되었습니다."
          : "알림이 성공적으로 발송되었습니다.";
        let title = isSave ? "알림 저장 완료" : "알림 발송 완료";
        if (response.statusCode === 200) {
          setSuccessMessage({ title: title, message: message });
          setShowSuccessDialog(true); // 성공 다이얼로그 표시
        } else {
          title = isSave ? "알림 저장 실패" : "알림 발송 실패";
          setSuccessMessage({ title: title, message: message });
          setShowSuccessDialog(true);
        }
        // 폼 초기화 (필요시)
        // formMethods.reset();
      },
      onError: (error) => {
        console.error(isSave ? "저장 실패:" : "발송 실패:", error);
        const errorMessage = isSave
          ? "알림 저장에 실패했습니다. 다시 시도해주세요."
          : "알림 발송에 실패했습니다. 다시 시도해주세요.";
        alert(errorMessage);
      },
    });
  };

  // 모달에서 이미지 선택/해제
  const toggleImageSelection = (img: GalleryImage) => {
    setModalSelectedImages((prev) =>
      prev.find((item) => item.ImgID === img.ImgID)
        ? prev.filter((item) => item.ImgID !== img.ImgID)
        : [...prev, img]
    );
  };

  // 선택된 이미지 미리보기에서 삭제
  const removeSelectedImage = (img: GalleryImage) => {
    setSelectedImages((prev) =>
      prev.filter((item) => item.ImgID !== img.ImgID)
    );
    setModalSelectedImages((prev) =>
      prev.filter((item) => item.ImgID !== img.ImgID)
    );
  };

  // 모달에서 선택한 이미지 확정
  const handleImgModal = () => {
    // 기존 선택된 이미지와 새로 선택한 이미지를 합침
    const merged = [...selectedImages, ...modalSelectedImages];
    // ImgID 기준으로 중복 제거
    const unique = merged.filter(
      (img, idx, arr) => arr.findIndex((i) => i.ImgID === img.ImgID) === idx
    );
    setSelectedImages(unique);
    imageModal.closeModal();
  };

  // 네이티브 사진첩 결과(fileSeq/fileUrl 목록)를 선택목록에 추가 (중복 제거)
  const addBridgeImages = (photos: { fileSeq: number; fileUrl: string }[]) => {
    setSelectedImages((prev) => {
      const merged = [
        ...prev,
        ...photos.map((p) => ({ ImgID: p.fileSeq, ImgURL: p.fileUrl })),
      ];
      return merged.filter(
        (img, idx, arr) => arr.findIndex((i) => i.ImgID === img.ImgID) === idx,
      );
    });
  };

  return {
    // 폼 관련
    formMethods,

    // 선택 목록 데이터
    selectListData,

    // 유효성 검사 함수
    validateActivitySelections,

    // noticeSendSubData 관련
    noticeSendSubData,
    setNoticeSendSubData,

    // 날짜 관련
    date,
    selectedDate,
    setSelectedDate,
    selectedImgDate,
    setSelectedImgDate,

    // 모달 관련
    calendarModal,
    imageModal,
    imgDateModal,

    // 이미지 관련
    selectedImages,
    modalSelectedImages,
    toggleImageSelection,
    removeSelectedImage,
    handleImgModal,
    addBridgeImages,

    // 앨범 관련
    albumData,
    fetchAlbumList,

    // 토글 관련
    isToggle,
    setIsToggle,
    isTimeToggle,
    setIsTimeToggle,
    handleTimeToggleChange,

    // 다이얼로그 관련
    showSuccessDialog,
    setShowSuccessDialog,
    successMessage,

    // 제출 관련
    bindSubmit,
    isSubmitting: sendNoticeMutation.isPending,
    submitError: sendNoticeMutation.error,
  };
};

export default useNotifySendInform;
